"use client"

import { useEffect } from "react"
import { getSearchResultsHashProbe } from "openapi/api/endpoints"
import { GetSearchResultsHashProbe200 } from "openapi/api/model"

const MAX_PROBE_ATTEMPTS = 25

interface UseWarmNextSearchResultsPageOptions {
  currentPage?: number | string | null
  enabled?: boolean
  searchHash?: string | null
}

function normalizePage(page: UseWarmNextSearchResultsPageOptions["currentPage"]) {
  if (typeof page === "number" && Number.isInteger(page) && page > 0) {
    return page
  }

  if (typeof page === "string") {
    const normalized = Number.parseInt(page, 10)

    if (Number.isInteger(normalized) && normalized > 0) {
      return normalized
    }
  }

  return null
}

function shouldContinueProbing(response?: GetSearchResultsHashProbe200) {
  if (!response) {
    return false
  }

  if (response.enough_for_requested_page) {
    return false
  }

  if (response.can_extend === false) {
    return false
  }

  if (response.provider_page_cap_reached) {
    return false
  }

  return true
}

export function useWarmNextSearchResultsPage({
  currentPage,
  enabled = true,
  searchHash,
}: UseWarmNextSearchResultsPageOptions) {
  const normalizedCurrentPage = normalizePage(currentPage)
  const nextPage = normalizedCurrentPage === null ? null : normalizedCurrentPage + 1

  useEffect(() => {
    if (!enabled || !searchHash || nextPage === null) {
      return
    }

    const controller = new AbortController()

    const warmNextPage = async () => {
      try {
        let attempts = 0
        let response: GetSearchResultsHashProbe200 | undefined

        do {
          attempts += 1
          response = await getSearchResultsHashProbe(
            searchHash,
            { page: String(nextPage) },
            undefined,
            controller.signal,
          )
        } while (shouldContinueProbing(response) && attempts < MAX_PROBE_ATTEMPTS)
      } catch (error) {
        if (controller.signal.aborted) {
          return
        }

        console.error(`Failed to warm search results page ${nextPage} for ${searchHash}`, error)
      }
    }

    void warmNextPage()

    return () => {
      controller.abort()
    }
  }, [enabled, nextPage, searchHash])
}
